import clsx from 'clsx';
import * as React from 'react';
import { useInteractOutside, useObjectRef } from 'react-aria';

import { selectInline } from '@gitbook/doc-core';
import { findDOMNode, type InlineProps } from '@gitbook/slate-react';
import { shouldCloseOnInteractOutsideOverlay } from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assert } from '@gitbook/util-ts';

import { InlineEditingContext } from './InlineEditingContext';

/**
 * Editable annotation.
 */
export function useInlineEdit(
    props: Pick<InlineProps, 'node' | 'editor'> & {
        isOpen: boolean;
        onOpenChange: (isOpen: boolean) => void;
        onEdit: (isEditing: boolean) => void;
        onCancel?: () => void;
        isEditing: boolean;
    }
) {
    const { node, editor, isOpen, onOpenChange, onEdit, onCancel, isEditing } = props;

    const onExitedRef = React.useRef<(() => void) | null>(null);

    const focusEndOfInline = () => {
        editor.change((change) => {
            selectInline(change, node);
            change.collapseToStartOfNextText();
        });
    };

    const cancel = (args: { focusEndOfInline: boolean }) => {
        onExitedRef.current = () => {
            onCancel?.();
            onEdit(false);
        };
        onOpenChange(false);
        if (args.focusEndOfInline) {
            focusEndOfInline();
        }
    };

    const submit = () => {
        onExitedRef.current = () => {
            onEdit(false);
        };
        onOpenChange(false);
        focusEndOfInline();
    };

    const edit = () => {
        onOpenChange(true);
        onEdit(true);
    };

    const popoverRef = useObjectRef<HTMLDivElement>(null);
    useInteractOutside({
        ref: popoverRef,
        isDisabled: !isEditing || !isOpen,
        onInteractOutside(event) {
            // First fast check if we can close with overlay rules.
            if (
                !(event.target instanceof Element) ||
                !shouldCloseOnInteractOutsideOverlay(event.target)
            ) {
                return;
            }

            // Then check if the users interacts with the inline node
            // in the editor. In this case, instantely cancel.
            const domNode = findDOMNode(node.key);
            assert(domNode);
            if (domNode.contains(event.target)) {
                onCancel?.();
                onEdit(false);
                return;
            }

            cancel({ focusEndOfInline: false });
        },
    });

    const popoverRefHandler = useEventCallback((element: HTMLDivElement | null) => {
        popoverRef.current = element;
        // When the element is unmouted, it means all animations are done
        // we want to change the state once it's invisible to avoid glitches.
        if (!element && onExitedRef.current) {
            onExitedRef.current?.();
            onExitedRef.current = null;
        }
    });

    return {
        popoverRootProps: {
            triggerAction: 'hover' as const,
            triggerDelay: 600,
            isOpen,
            onOpenChange: (isOpen) => {
                // If we are in editing mode, we control it ourself.
                if (!isEditing) {
                    onOpenChange(isOpen);
                }
            },
        },
        popoverProps: {
            ref: popoverRefHandler,
            isDismissable: false,
            onKeyDownCapture: (event: React.KeyboardEvent) => {
                if (event.key === 'Escape') {
                    event.stopPropagation();
                    cancel({ focusEndOfInline: true });
                }
            },
        },
        cancel,
        submit,
        edit,
    };
}

/**
 * Props to apply when a low-level inline tag is edited.
 */
export function useInlineTagProps(props: React.HTMLAttributes<HTMLElement>) {
    const isEditing = React.useContext(InlineEditingContext);
    if (isEditing) {
        return { ...props, className: clsx('-mx-1 rounded bg-active px-1', props.className) };
    }
    return props;
}
